import { router } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { MagicCard } from "../../../components/magicui/magic-card";
import { SparklesText } from "../../../components/magicui/sparkles-text";
import { cn } from "../../../lib/utils";

const DISCOUNT_CODE_TYPES = [
  { value: "ambassador_free_access", label: "Ambassador free access" },
  { value: "percentage_off", label: "Percentage off" },
  { value: "fixed_amount_off", label: "Fixed amount off" },
  { value: "free_months", label: "Free months" }
];

const DISCOUNT_DURATION_UNITS = [
  { value: "months", label: "Months" },
  { value: "years", label: "Years" }
];

function buildDiscountCodeDraft() {
  return {
    code: "",
    kind: "ambassador_free_access",
    plan_id: "",
    duration_value: "1",
    duration_unit: "months",
    percent_off: "20",
    amount_off: "10",
    free_months: "1",
    recipient_email: "",
    active: true
  };
}

export default function AdminDiscountCodesIndex({ plans: initialPlans = [], discount_codes: initialDiscountCodes = [], auth }) {
  const [plans] = useState(initialPlans);
  const [discountCodes, setDiscountCodes] = useState(initialDiscountCodes);
  const [discountCodeDraft, setDiscountCodeDraft] = useState(() => buildDiscountCodeDraft());
  const [discountEmailDrafts, setDiscountEmailDrafts] = useState({});
  const [discountLoading, setDiscountLoading] = useState({ create: false, toggleId: null, sendId: null });
  const [feedback, setFeedback] = useState({ kind: "info", message: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const creditPlans = plans.filter((plan) => plan.billing_mode === "credits");
  const displayName = auth?.name?.trim() || auth?.email?.split("@")[0] || "Account";
  const dropdownItemBaseClass =
    "flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 focus-visible:outline-none";
  const dropdownItemIdleClass =
    "text-slate-200 hover:translate-x-0.5 hover:bg-slate-800 hover:text-white hover:shadow-[0_10px_20px_rgba(2,6,23,.26)] focus-visible:bg-slate-800 focus-visible:text-white";
  const dropdownSuperadminClass =
    "bg-[linear-gradient(135deg,rgba(168,85,247,.16),rgba(59,130,246,.1))] text-violet-100 shadow-[inset_0_0_0_1px_rgba(168,85,247,.08),0_10px_18px_rgba(2,6,23,.16)]";
  const dropdownSignOutClass =
    "text-rose-100 hover:translate-x-0.5 hover:bg-rose-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(190,24,93,.16)] focus-visible:bg-rose-500/18 focus-visible:text-white";
  const adminMenuItems = [
    { label: "Generation", href: "/app" },
    { label: "Account", href: "/app/account" },
    { label: "Billing", href: "/app/billing" },
    { label: "Settings", href: "/app/settings" }
  ];

  useEffect(() => {
    setDiscountCodes(Array.isArray(initialDiscountCodes) ? initialDiscountCodes : []);
    setDiscountEmailDrafts(
      Object.fromEntries(
        (Array.isArray(initialDiscountCodes) ? initialDiscountCodes : []).map((code) => [code.id, code.recipient_email || ""])
      )
    );
  }, [initialDiscountCodes]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  const updateDiscountCodeDraft = (key, value) => {
    setDiscountCodeDraft((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const updateDiscountEmailDraft = (discountCodeId, value) => {
    setDiscountEmailDrafts((prev) => ({
      ...prev,
      [discountCodeId]: value
    }));
  };

  const createDiscountCode = async () => {
    setDiscountLoading((prev) => ({ ...prev, create: true }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch("/admin/discount_codes", {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({ discount_code: discountCodeDraft })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Discount code creation failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setDiscountCodeDraft(buildDiscountCodeDraft());
      setDiscountEmailDrafts((prev) => ({
        ...prev,
        [data.discount_code?.id]: data.discount_code?.recipient_email || ""
      }));
      setFeedback({ kind: "ok", message: data.message || "Discount code created." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Discount code creation failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const toggleDiscountCodeActive = async (discountCode) => {
    setDiscountLoading((prev) => ({ ...prev, toggleId: discountCode.id }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch(`/admin/discount_codes/${discountCode.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          discount_code: {
            active: !discountCode.active
          }
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Discount code update failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setFeedback({ kind: "ok", message: data.message || "Discount code updated." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Discount code update failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, toggleId: null }));
    }
  };

  const sendDiscountCodeInstructions = async (discountCodeId) => {
    setDiscountLoading((prev) => ({ ...prev, sendId: discountCodeId }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch(`/admin/discount_codes/${discountCodeId}/send_instructions`, {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          recipient_email: discountEmailDrafts[discountCodeId] || ""
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Instruction email failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setDiscountEmailDrafts((prev) => ({
        ...prev,
        [discountCodeId]: data.discount_code?.recipient_email || prev[discountCodeId] || ""
      }));
      setFeedback({ kind: "ok", message: data.message || "Instruction email queued." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Instruction email failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, sendId: null }));
    }
  };

  return (
    <main className="min-h-screen bg-black px-6 py-8 text-white sm:px-8">
      <div className="mx-auto max-w-7xl space-y-4">
        <header className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-10 w-10 shrink-0 md:h-12 md:w-12" />
            <div>
              <h1 className="text-base font-bold tracking-tight md:text-base">
                <SparklesText textClassName="font-bold tracking-tight">UGC Persona</SparklesText>
              </h1>
              <p className="mt-0.5 text-[11px] font-medium text-slate-300 md:text-xs">Your AI influencer. Your brand. Infinite content.</p>
            </div>
          </div>

          {auth ? (
            <div className="relative shrink-0">
              <button
                ref={menuButtonRef}
                type="button"
                aria-expanded={menuOpen}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-400/35 bg-slate-950/70 px-1.5 py-1.5 shadow-[0_0_0_1px_rgba(8,145,178,.12),0_10px_24px_rgba(2,6,23,.28)] transition hover:border-cyan-300/55 hover:bg-slate-900/80"
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <img src={auth.avatar_url} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
                <svg
                  viewBox="0 0 20 20"
                  className={cn("h-4 w-4 text-slate-300 transition-transform", menuOpen && "rotate-180")}
                  aria-hidden
                >
                  <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {menuOpen ? (
                <div
                  ref={menuRef}
                  className="absolute right-0 top-full z-50 mt-2 w-[250px] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/95 p-2 shadow-[0_18px_40px_rgba(2,6,23,.55)] backdrop-blur"
                >
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2.5">
                    <p className="truncate text-sm font-semibold text-slate-100">{displayName}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">{auth.email}</p>
                  </div>

                  <div className="mt-2 space-y-1">
                    {adminMenuItems.map((item) => (
                      <button
                        key={`menu-${item.href}`}
                        type="button"
                        className={cn(dropdownItemBaseClass, dropdownItemIdleClass)}
                        onClick={() => {
                          setMenuOpen(false);
                          router.visit(item.href);
                        }}
                      >
                        <span>{item.label}</span>
                      </button>
                    ))}

                    <button
                      type="button"
                      className={cn(dropdownItemBaseClass, dropdownSuperadminClass)}
                      onClick={() => {
                        setMenuOpen(false);
                        router.visit("/admin/settings");
                      }}
                    >
                      <span>Superadmin Settings</span>
                    </button>

                    <div className="my-1 h-px bg-slate-800" />

                    <button
                      type="button"
                      className={cn(dropdownItemBaseClass, dropdownSignOutClass)}
                      onClick={() => {
                        setMenuOpen(false);
                        router.delete("/users/sign_out");
                      }}
                    >
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </header>

        <header className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-800 bg-slate-950/90 p-6 shadow-[0_24px_70px_rgba(2,6,23,.55)]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Superadmin</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Discount Codes</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
              Create ambassador access codes, Stripe discounts, and free-month signup offers. After generation, send instructions directly by email to the recipient.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => router.visit("/admin/settings")}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
            >
              Back to settings
            </button>
            <button
              type="button"
              onClick={() => router.visit("/app")}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
            >
              Open app
            </button>
          </div>
        </header>

        {feedback.message ? (
          <div
            className={cn(
              "rounded-2xl border px-4 py-3 text-sm shadow-[0_12px_32px_rgba(2,6,23,.35)]",
              feedback.kind === "ok"
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
                : feedback.kind === "error"
                  ? "border-rose-400/30 bg-rose-400/10 text-rose-100"
                  : "border-slate-700 bg-slate-900/80 text-slate-200"
            )}
          >
            {feedback.message}
          </div>
        ) : null}

        <MagicCard className="lg:col-span-2">
          <div className="grid gap-4 lg:grid-cols-[1.05fr_1fr]">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
              <h2 className="section-title">Create code</h2>
              <p className="mt-1 text-sm text-slate-400">
                Ambassador codes skip Stripe entirely. All other codes are applied automatically during signup and checkout.
              </p>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="label-ui">
                  Code
                  <input
                    className="input-ui font-mono text-xs uppercase"
                    type="text"
                    value={discountCodeDraft.code}
                    onChange={(event) => updateDiscountCodeDraft("code", event.target.value.toUpperCase())}
                    placeholder="Leave empty to auto-generate"
                  />
                </label>

                <label className="label-ui">
                  Type
                  <select className="input-ui" value={discountCodeDraft.kind} onChange={(event) => updateDiscountCodeDraft("kind", event.target.value)}>
                    {DISCOUNT_CODE_TYPES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="label-ui">
                  Plan
                  <select className="input-ui" value={discountCodeDraft.plan_id} onChange={(event) => updateDiscountCodeDraft("plan_id", event.target.value)}>
                    <option value="">Any credit plan</option>
                    {creditPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="label-ui">
                  Recipient email
                  <input
                    className="input-ui"
                    type="email"
                    value={discountCodeDraft.recipient_email}
                    onChange={(event) => updateDiscountCodeDraft("recipient_email", event.target.value)}
                    placeholder="recipient@example.com"
                  />
                </label>

                {discountCodeDraft.kind === "ambassador_free_access" ? (
                  <>
                    <label className="label-ui">
                      Duration
                      <input
                        className="input-ui"
                        type="number"
                        min="1"
                        step="1"
                        value={discountCodeDraft.duration_value}
                        onChange={(event) => updateDiscountCodeDraft("duration_value", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Duration unit
                      <select
                        className="input-ui"
                        value={discountCodeDraft.duration_unit}
                        onChange={(event) => updateDiscountCodeDraft("duration_unit", event.target.value)}
                      >
                        {DISCOUNT_DURATION_UNITS.map((unit) => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                ) : null}

                {discountCodeDraft.kind === "percentage_off" ? (
                  <label className="label-ui">
                    Percent off
                    <input
                      className="input-ui"
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      value={discountCodeDraft.percent_off}
                      onChange={(event) => updateDiscountCodeDraft("percent_off", event.target.value)}
                    />
                  </label>
                ) : null}

                {discountCodeDraft.kind === "fixed_amount_off" ? (
                  <label className="label-ui">
                    Amount off (€)
                    <input
                      className="input-ui"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={discountCodeDraft.amount_off}
                      onChange={(event) => updateDiscountCodeDraft("amount_off", event.target.value)}
                    />
                  </label>
                ) : null}

                {discountCodeDraft.kind === "free_months" ? (
                  <label className="label-ui">
                    Free months
                    <input
                      className="input-ui"
                      type="number"
                      min="1"
                      step="1"
                      value={discountCodeDraft.free_months}
                      onChange={(event) => updateDiscountCodeDraft("free_months", event.target.value)}
                    />
                  </label>
                ) : null}
              </div>

              <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
                {discountCodeDraft.kind === "ambassador_free_access"
                  ? "Ambassador codes create a fully active subscription immediately and skip Stripe entirely."
                  : discountCodeDraft.kind === "free_months"
                    ? "Free-month codes send the user to Stripe checkout with the trial period applied automatically."
                    : "Discount codes are applied automatically during Stripe checkout once the user signs up with the code."}
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={createDiscountCode}
                  disabled={discountLoading.create}
                  className="inline-flex items-center justify-center rounded-xl bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2.5 text-sm font-semibold text-slate-50 shadow-[0_14px_32px_rgba(2,6,23,.32)] transition hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {discountLoading.create ? "Creating..." : "Generate discount code"}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {discountCodes.length ? (
                discountCodes.map((code) => (
                  <div key={code.id} className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">{code.code}</p>
                        <p className="mt-1 text-sm font-semibold text-slate-100">{code.instructions_summary}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-400">{code.signup_hint}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleDiscountCodeActive(code)}
                        disabled={discountLoading.toggleId === code.id}
                        className={cn(
                          "rounded-xl px-3 py-2 text-xs font-semibold transition",
                          code.active
                            ? "border border-emerald-400/35 bg-emerald-500/15 text-emerald-100"
                            : "border border-slate-700 bg-slate-900/80 text-slate-300"
                        )}
                      >
                        {discountLoading.toggleId === code.id ? "Saving..." : code.active ? "Active" : "Inactive"}
                      </button>
                    </div>

                    <div className="mt-3 grid gap-2 text-xs text-slate-400 md:grid-cols-2">
                      <p>Type: <span className="text-slate-200">{code.kind_label}</span></p>
                      <p>Plan: <span className="text-slate-200">{code.plan?.name || "Any credit plan"}</span></p>
                      <p>Redemptions: <span className="text-slate-200">{code.redemptions_count} / {code.max_redemptions}</span></p>
                      <p>Created: <span className="text-slate-200">{formatIsoDateTime(code.created_at)}</span></p>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200">Send instructions</p>
                      <div className="mt-3 flex flex-col gap-3 md:flex-row">
                        <input
                          className="input-ui min-w-0 flex-1"
                          type="email"
                          value={discountEmailDrafts[code.id] || ""}
                          onChange={(event) => updateDiscountEmailDraft(code.id, event.target.value)}
                          placeholder="recipient@example.com"
                        />
                        <button
                          type="button"
                          onClick={() => sendDiscountCodeInstructions(code.id)}
                          disabled={discountLoading.sendId === code.id}
                          className="inline-flex items-center justify-center rounded-xl border border-cyan-400/35 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-cyan-300/50 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {discountLoading.sendId === code.id ? "Sending..." : "Send email"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 px-4 py-6 text-sm text-slate-400">
                  No discount codes yet. Generate one from the form on the left.
                </div>
              )}
            </div>
          </div>
        </MagicCard>
      </div>
    </main>
  );
}

function buildJsonHeaders() {
  return {
    "Content-Type": "application/json",
    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
  };
}

function firstError(errors) {
  if (!errors || typeof errors !== "object") return "";
  const values = Object.values(errors);
  const first = values[0];
  if (Array.isArray(first)) return first[0];
  return typeof first === "string" ? first : "";
}

function formatIsoDateTime(value) {
  if (!value) return "Not available";

  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "Not available";
  }
}

function BrandLogo({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="admin-discount-logo-gradient" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.5" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#020617" stroke="url(#admin-discount-logo-gradient)" strokeWidth="3" />
      <path
        d="M17 45L30.5 17.5a2 2 0 013 0L47 45a2 2 0 01-1.8 2.9H18.8A2 2 0 0117 45z"
        fill="none"
        stroke="url(#admin-discount-logo-gradient)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="33" r="5.5" fill="url(#admin-discount-logo-gradient)" />
    </svg>
  );
}
